"use client";

import { motion, stagger, useAnimate } from "motion/react";
import * as React from "react";
import {
	cn,
	resolveTextGenerateTiming,
	type TextGenerateSplitBy,
} from "../../lib";
import { useFadeInContext } from "../custom/fade-in-on-scroll";

const MAX_SCOPE_ATTACH_ATTEMPTS = 180;

export interface TextGenerateEffectProps
	extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
	words: string;
	filter?: boolean;
	duration?: number;
	staggerDelay?: number;
	/**
	 * Fixed wall-clock for the full LTR sequence (seconds). When set, stagger is
	 * derived from token count so short and long strings finish together.
	 */
	totalDuration?: number;
	/**
	 * `words` (default): stagger space-separated tokens (homepage copy).
	 * `characters`: stagger each character left-to-right (prices, compact figures).
	 */
	splitBy?: TextGenerateSplitBy;
	/** When true, animation begins only after {@link FadeInOnScroll} marks fade complete for this subtree. */
	respectFadeIn?: boolean;
	/** Extra delay (ms) after fade/readiness before starting word animation (sequencing segments). */
	delayBeforeAnimateMs?: number;
	as?: "div" | "span";
	fontBold?: boolean;
	/**
	 * Per-word extras (opacity/blur animation still runs per word).
	 * Omit when using {@link phraseGradientClassName} or {@link deferPhraseGradient} on the phrase wrapper.
	 */
	wordClassName?: string;
	/**
	 * Applies gradient text utilities once to the whole phrase wrapper (`bg-gradient-*`, `bg-clip-text`,
	 * `text-transparent`, font weight). Words animate individually underneath so the gradient reads as one band.
	 */
	phraseGradientClassName?: string;
	/**
	 * During stagger only: omit gradient on the phrase wrapper and use `text-current` so colour matches the block.
	 * Pair with deferring gradient until an outer wrapper swaps copy after {@link onAnimationComplete}.
	 */
	deferPhraseGradient?: boolean;
	onAnimationComplete?: () => void;
}

export const TextGenerateEffect = React.forwardRef<
	HTMLElement,
	TextGenerateEffectProps
>(function TextGenerateEffect(
	{
		words,
		className,
		filter = true,
		duration = 0.5,
		staggerDelay = 0.2,
		totalDuration,
		splitBy = "words",
		respectFadeIn = false,
		delayBeforeAnimateMs = 0,
		as = "div",
		fontBold = true,
		wordClassName,
		phraseGradientClassName,
		deferPhraseGradient = false,
		onAnimationComplete,
		...props
	},
	ref,
) {
	const [scope, animate] = useAnimate();
	const { isFadeInComplete } = useFadeInContext();

	type TextGenerateToken =
		| { kind: "word"; word: string; animKey: string }
		| { kind: "br"; id: string };

	const renderTokens = React.useMemo(() => {
		const segments = words
			.split(/\n/)
			.map((s) => s.trim())
			.filter(Boolean);
		const tokens: TextGenerateToken[] = [];
		let wordOrdinal = 0;
		for (let i = 0; i < segments.length; i += 1) {
			const segment = segments[i];
			if (!segment) {
				continue;
			}
			const units =
				splitBy === "characters"
					? Array.from(segment)
					: segment.split(/\s+/).filter(Boolean);
			for (const unit of units) {
				wordOrdinal += 1;
				tokens.push({
					kind: "word",
					word: unit,
					animKey: `w-${wordOrdinal}`,
				});
			}
			if (i < segments.length - 1) {
				tokens.push({ kind: "br", id: `nl-${i}` });
			}
		}
		return tokens;
	}, [splitBy, words]);

	const wordsArray = React.useMemo(
		() =>
			renderTokens
				.filter(
					(t): t is { kind: "word"; word: string; animKey: string } =>
						t.kind === "word",
				)
				.map((t) => t.word),
		[renderTokens],
	);

	const hasWordSpans = wordsArray.length > 0;
	const canAnimate = !respectFadeIn || isFadeInComplete;

	const Outer = as === "span" ? "span" : "div";

	const timing = React.useMemo(
		() =>
			resolveTextGenerateTiming({
				tokenCount: wordsArray.length,
				duration,
				staggerDelay,
				totalDuration,
			}),
		[duration, staggerDelay, totalDuration, wordsArray.length],
	);

	const onAnimationCompleteRef = React.useRef(onAnimationComplete);
	onAnimationCompleteRef.current = onAnimationComplete;

	React.useLayoutEffect(() => {
		if (!canAnimate) {
			return;
		}

		if (!hasWordSpans) {
			queueMicrotask(() => {
				onAnimationCompleteRef.current?.();
			});
			return;
		}

		let cancelled = false;
		let delayTimeoutId: ReturnType<typeof setTimeout> | undefined;
		let completeId: ReturnType<typeof setTimeout> | undefined;
		let rafId = 0;
		let attachAttempts = 0;

		const runStagger = () => {
			if (cancelled || !scope.current) {
				return;
			}

			void animate(
				"span",
				{
					opacity: 1,
					filter: filter ? "blur(0px)" : "none",
				},
				{
					duration: timing.duration,
					delay: stagger(timing.staggerDelay),
				},
			);

			if (onAnimationCompleteRef.current) {
				completeId = setTimeout(() => {
					if (!cancelled) {
						onAnimationCompleteRef.current?.();
					}
				}, timing.completeMs);
			}
		};

		const runWhenScoped = () => {
			if (cancelled) {
				return;
			}
			if (!scope.current) {
				if (attachAttempts >= MAX_SCOPE_ATTACH_ATTEMPTS) {
					return;
				}
				attachAttempts += 1;
				rafId = requestAnimationFrame(runWhenScoped);
				return;
			}

			runStagger();
		};

		const queueRun = () => {
			if (cancelled) {
				return;
			}
			if (delayBeforeAnimateMs > 0) {
				delayTimeoutId = setTimeout(
					runWhenScoped,
					delayBeforeAnimateMs,
				);
			} else {
				runWhenScoped();
			}
		};

		queueRun();

		return () => {
			cancelled = true;
			if (delayTimeoutId) {
				clearTimeout(delayTimeoutId);
			}
			if (completeId) {
				clearTimeout(completeId);
			}
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [
		animate,
		canAnimate,
		delayBeforeAnimateMs,
		filter,
		hasWordSpans,
		scope,
		timing.completeMs,
		timing.duration,
		timing.staggerDelay,
	]);

	if (!hasWordSpans) {
		return null;
	}

	return (
		<Outer
			ref={ref as React.Ref<HTMLDivElement & HTMLSpanElement>}
			className={cn(
				fontBold && "font-bold",
				as === "span" && "inline",
				className,
			)}
			data-slot="text-generate-effect"
			{...props}
			aria-label={
				props["aria-label"] ??
				(splitBy === "characters" ? words : undefined)
			}
		>
			<motion.span
				ref={scope}
				className={cn(
					as === "span" ? "inline" : "block",
					splitBy === "characters" && "whitespace-pre",
					deferPhraseGradient
						? "text-current"
						: phraseGradientClassName,
				)}
			>
				{renderTokens.map((tok, idx) =>
					tok.kind === "br" ? (
						<br key={tok.id} />
					) : (
						<motion.span
							className={cn(
								"will-change-[opacity,filter,transform]",
								as === "span" && "inline",
								!phraseGradientClassName &&
									!deferPhraseGradient &&
									wordClassName,
							)}
							initial={{
								opacity: 0,
								filter: filter ? "blur(10px)" : "none",
							}}
							key={tok.animKey}
						>
							{tok.word}
							{splitBy === "words" &&
							renderTokens[idx + 1]?.kind === "word"
								? " "
								: ""}
						</motion.span>
					),
				)}
			</motion.span>
		</Outer>
	);
});

TextGenerateEffect.displayName = "TextGenerateEffect";
